
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import BidsWaitListFilters from '@/modules/BidsModule/components/BidsWaitListFilters/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// module components
import UpdateSignersModal from '@/modules/ContractsModule/components/UpdateSignersModal/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { BidOnSignT, BidsGetBidsOnSignParamsT } from '@/core/types/bids.types'
import { TabT } from '@/core/types/components.types'

// realisations
import { bids } from '@/core/realisations/bids.realisation'
import { contracts } from '@/core/realisations/contracts.realisation'

// constants
import { BidsWaitListColumns } from '@/core/constants/columns/BidsList.columns'

// composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'bids-wait-list-filters': BidsWaitListFilters,
        'update-signers-modal': UpdateSignersModal,
        TheTable,
        TableScrollWrapper,
        TheInput,
        TheSelect,
    },
    setup() {
        const notifications = useNotifications()

        const delete_state = reactive({
            is_loading: false,
        })

        const check_state = reactive({
            is_loading: false,
        })

        const edit_state = reactive({
            is_loading: false,
            is_open: false,
            id: null as number | null,
            signers: [] as { email: string; isSigned: boolean; isLawyer: boolean; typeId: number }[],
        })

        const data_state = reactive(new DataState<BidOnSignT>({}))

        const filters_state = reactive({
            limit: 50,
            //
            bin: null as string | null,
            companyShortName: null as string | null,
            email: null as string | null,
            createdAt: null as string | null,
        })

        const tabs: TabT[] = [
            { id: 1, name: 'Список заявок', path: '/bids' },
            { id: 2, name: 'На согласовании', path: '/bids/to-be-agreed' },
        ]

        const loadBidsOnSign = async (page: number) => {
            data_state.pagination.current_page = page

            try {
                data_state.is_loading = true

                let params = {
                    page,
                    bin: filters_state.bin,
                    companyShortName: filters_state.companyShortName,
                    email: filters_state.email,
                    createdAt: filters_state.createdAt,
                    limit: filters_state.limit,
                }

                const response = await bids.getBidsOnSign(params)
                data_state.list = response.data
                data_state.pagination.total_pages = response.meta.last_page
                data_state.pagination.total_items = response.meta.total

                data_state.is_failed = false
                data_state.is_completed = true
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const getContractStatus = (is_signed: boolean, type_id: number): string => {
            if (is_signed && type_id === 1) return 'подписал'
            if (is_signed && type_id === 2) return 'согласовал'

            if (!is_signed && type_id === 1) return 'не подписал'
            if (!is_signed && type_id === 2) return 'не согласовал'

            return ''
        }

        const applyFilters = (params: BidsGetBidsOnSignParamsT) => {
            filters_state.bin = params.bin || null
            filters_state.companyShortName = params.companyShortName || null
            filters_state.email = params.email || null
            filters_state.createdAt = params.createdAt || null

            loadBidsOnSign(data_state.pagination.current_page)
        }

        const resetFilters = () => {
            filters_state.bin = null
            filters_state.companyShortName = null
            filters_state.email = null
            filters_state.createdAt = null

            loadBidsOnSign(data_state.pagination.current_page)
        }

        const deleteBid = async (contract_id: number) => {
            const answer = confirm('Вы уверены, что хотите удалить заявку?')
            if (!answer) return

            try {
                delete_state.is_loading = true
                await contracts.deleteContract(contract_id)

                notifications.notify({
                    title: 'Заявка удалена',
                    type: 'success',
                })
            } catch (error: any) {
                if (error.length) {
                    notifications.notifyError({
                        error,
                        title: 'При удалении заявки произошла ошибка',
                    })
                } else {
                    notifications.notify({
                        title: 'При удалении заявки произошла ошибка',
                        type: 'danger',
                    })
                }
            } finally {
                delete_state.is_loading = false

                loadBidsOnSign(1)
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadBidsOnSign(data_state.pagination.current_page)
        }

        const checkIsSigned = async (id: number) => {
            try {
                check_state.is_loading = true
                await contracts.checkIsSigned(id)

                loadBidsOnSign(1)
                notifications.notify({
                    title: 'Заявка успешно проверена!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке заявки произошла ошибка',
                })
            } finally {
                check_state.is_loading = false
            }
        }

        const toggleEditSigners = (item?: BidOnSignT) => {
            if (item) {
                edit_state.id = item.id
                edit_state.signers = item.signers.map((signer) => {
                    return {
                        email: signer.email,
                        typeId: signer.typeId,
                        isLawyer: signer.isLawyer,
                        isSigned: signer.isSigned,
                    }
                })
            }
            edit_state.is_open = !edit_state.is_open
        }

        const editSigners = async (signersData: { email: string; newEmail: string | null; dogovor24TypeId: number;}[]) => {
            try {
                edit_state.is_loading = true

                await bids.editSigners(edit_state.id as number, { signersData })

                notifications.notify({
                    title: 'Редактирование успешно завершено!',
                    type: 'success',
                })

                toggleEditSigners()
                loadBidsOnSign(data_state.pagination.current_page)
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При редактировании произошла ошибка',
                })
            } finally {
                edit_state.is_loading = false
            }
        }

        //

        onBeforeMount(() => loadBidsOnSign(1))

        //

        return {
            data_state,
            filters_state,
            delete_state,
            check_state,
            edit_state,
            //
            BidsWaitListColumns,
            loadBidsOnSign,
            getContractStatus,
            applyFilters,
            resetFilters,
            deleteBid,
            onLimitChange,
            checkIsSigned,
            toggleEditSigners,
            editSigners,
            //
            tabs,
        }
    },
})
